import { useState } from 'react';
import { 
  FaMapMarkerAlt, 
  FaPaperPlane,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaHeadset,
  FaLaptop
} from 'react-icons/fa';

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: ''
  });
  const [resume, setResume] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', { ...formData, resume });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setResume(e.target.files[0]);
  };

  const positions = [
    {
      title: 'Senior French Language Instructor',
      type: 'Full-time',
      location: 'Bengaluru, India',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'We are seeking an experienced French language instructor with DELF/DALF certification to teach A1-C2 levels.',
      fullDescription: 'We are looking for an experienced and passionate Senior French Language Instructor to join our growing team. The ideal candidate will have extensive knowledge of French language and culture, proven teaching experience, and DELF/DALF certification.',
      responsibilities: [
        'Teach French language courses from A1 to C2 levels',
        'Develop and implement engaging lesson plans and curriculum materials',
        'Conduct regular assessments and provide constructive feedback',
        'Prepare students for DELF/DALF examinations',
        'Organize cultural activities and workshops',
        'Maintain accurate records of student progress',
        'Collaborate with other instructors to enhance teaching methodologies'
      ],
      requirements: [
        'Native or near-native French proficiency (C2 level)',
        'DELF/DALF examiner certification preferred',
        'Minimum 5 years of teaching experience',
        'Bachelor\'s degree in French, Linguistics, or related field',
        'Master\'s degree preferred',
        'Excellent communication and interpersonal skills',
        'Familiarity with modern teaching tools and digital platforms',
        'Ability to teach both in-person and online classes'
      ],
      benefits: [
        'Competitive salary package',
        'Health insurance coverage',
        'Professional development opportunities',
        'Paid time off and holidays',
        'Performance-based bonuses',
        'Collaborative work environment'
      ]
    },
    {
      title: 'German Language Trainer',
      type: 'Full-time',
      location: 'Bengaluru, India',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'Join our team as a German language trainer. Goethe Institute certification preferred. Experience with business German is a plus.',
      fullDescription: 'The Lingua Alliance is seeking a dedicated German Language Trainer to deliver high-quality instruction to students of all levels. The successful candidate will have Goethe Institute certification and experience teaching business German.',
      responsibilities: [
        'Conduct German language classes for A1-C1 levels',
        'Design engaging course materials and learning activities',
        'Prepare students for Goethe Institute examinations',
        'Teach business German and professional communication',
        'Provide individual feedback and mentoring',
        'Stay updated with latest teaching methodologies',
        'Participate in curriculum development and improvement'
      ],
      requirements: [
        'Native or near-native German proficiency',
        'Goethe Institute certification (ZOP/GDS preferred)',
        'Minimum 3 years teaching experience',
        'Experience with business German instruction',
        'Bachelor\'s degree in German, Education, or related field',
        'Strong classroom management skills',
        'Proficiency in English for administrative communication',
        'Comfortable with online teaching platforms'
      ],
      benefits: [
        'Attractive compensation package',
        'Health and wellness benefits',
        'Continuous learning opportunities',
        'Annual leave and sick leave',
        'Festival bonuses',
        'Supportive team culture'
      ]
    },
    {
      title: 'Spanish Language Instructor',
      type: 'Part-time',
      location: 'Remote / Bengaluru',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'Part-time Spanish instructor position. DELE examiner credentials preferred. Flexible schedule available.',
      fullDescription: 'We are hiring a Part-time Spanish Language Instructor who is passionate about teaching and has DELE examiner credentials. This role offers flexible scheduling with both remote and in-person teaching options.',
      responsibilities: [
        'Teach Spanish language courses (A1-B2 levels)',
        'Create interactive and culturally rich lesson plans',
        'Assess student progress through regular evaluations',
        'Prepare students for DELE examinations',
        'Conduct conversation clubs and cultural sessions',
        'Provide personalized learning support',
        'Contribute to curriculum enhancement'
      ],
      requirements: [
        'Native or near-native Spanish proficiency',
        'DELE examiner credentials preferred',
        'Minimum 2 years of teaching experience',
        'Bachelor\'s degree in Spanish, Linguistics, or Education',
        'Strong understanding of Hispanic cultures',
        'Excellent communication skills',
        'Tech-savvy with online teaching tools',
        'Flexible and adaptable teaching approach'
      ],
      benefits: [
        'Flexible working hours',
        'Competitive hourly rates',
        'Remote work options',
        'Professional development support',
        'Access to teaching resources',
        'Collaborative teaching community'
      ]
    },
    {
      title: 'Japanese Language Instructor',
      type: 'Full-time',
      location: 'Bengaluru, India',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'JLPT certified instructor needed for N5-N1 levels. Experience with business Japanese and cultural training required.',
      fullDescription: 'Seeking an experienced Japanese Language Instructor with JLPT certification to teach comprehensive Japanese language courses. The ideal candidate will have expertise in business Japanese and cultural training.',
      responsibilities: [
        'Deliver Japanese language instruction from N5 to N1 levels',
        'Develop curriculum incorporating language and cultural elements',
        'Prepare students for JLPT examinations',
        'Teach business Japanese and keigo (honorific language)',
        'Organize cultural immersion activities',
        'Monitor and report student progress',
        'Mentor students pursuing careers requiring Japanese proficiency'
      ],
      requirements: [
        'Native Japanese speaker or JLPT N1 certified',
        'Minimum 4 years of teaching experience',
        'Bachelor\'s degree in Japanese Studies, Education, or related field',
        'Experience teaching business Japanese',
        'Deep understanding of Japanese culture and etiquette',
        'Proficient in English for administrative purposes',
        'Experience with both traditional and modern teaching methods',
        'Ability to teach in-person and online'
      ],
      benefits: [
        'Excellent salary package',
        'Comprehensive health insurance',
        'Annual performance bonuses',
        'Visa sponsorship if required',
        'Professional development budget',
        'Paid vacation and public holidays'
      ]
    },
    {
      title: 'Academic Coordinator',
      type: 'Full-time',
      location: 'Bengaluru, India',
      department: 'Administration',
      icon: <FaGraduationCap />,
      description: 'Manage academic programs, coordinate with faculty, and ensure curriculum quality. Master\'s degree in Linguistics or Education preferred.',
      fullDescription: 'The Academic Coordinator will oversee all academic operations, ensuring the highest quality of education delivery across all language programs. This role requires strong organizational and leadership skills.',
      responsibilities: [
        'Oversee academic program development and implementation',
        'Coordinate with language instructors and faculty',
        'Ensure curriculum quality and alignment with standards',
        'Monitor student performance and satisfaction',
        'Manage class scheduling and resource allocation',
        'Conduct faculty training and development programs',
        'Handle academic grievances and quality assurance',
        'Prepare reports and analytics for management'
      ],
      requirements: [
        'Master\'s degree in Linguistics, Education, or related field',
        'Minimum 5 years experience in academic administration',
        'Strong understanding of language education frameworks',
        'Excellent organizational and project management skills',
        'Proficiency in multiple languages preferred',
        'Strong leadership and team management abilities',
        'Experience with educational technology platforms',
        'Excellent written and verbal communication skills'
      ],
      benefits: [
        'Competitive salary with annual increments',
        'Health insurance for self and family',
        'Professional development opportunities',
        'Performance incentives',
        'Paid leave and holidays',
        'Retirement benefits'
      ]
    },
    {
      title: 'Customer Support Representative',
      type: 'Full-time',
      location: 'Bengaluru, India',
      department: 'Support',
      icon: <FaHeadset />,
      description: 'Multilingual customer support role. Must be fluent in English and at least one other language (French, German, Spanish, or Japanese).',
      fullDescription: 'Join our support team to assist students and clients with inquiries, course information, and technical support. Multilingual skills are essential for this role.',
      responsibilities: [
        'Handle customer inquiries via phone, email, and chat',
        'Provide information about courses, schedules, and fees',
        'Assist with enrollment and registration processes',
        'Resolve customer complaints and issues promptly',
        'Maintain accurate customer records in CRM system',
        'Coordinate with academic team for student concerns',
        'Support marketing initiatives and promotional campaigns',
        'Gather customer feedback for service improvement'
      ],
      requirements: [
        'Fluency in English and at least one other language (French/German/Spanish/Japanese)',
        'Minimum 2 years in customer service role',
        'Excellent communication and interpersonal skills',
        'Bachelor\'s degree in any field',
        'Proficiency with CRM software and Microsoft Office',
        'Problem-solving mindset and patience',
        'Ability to work in shifts if required',
        'Professional phone etiquette'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance coverage',
        'Performance bonuses',
        'Career growth opportunities',
        'Friendly work environment',
        'Employee engagement activities'
      ]
    },
    {
      title: 'Online Learning Platform Developer',
      type: 'Full-time',
      location: 'Remote / Bengaluru',
      department: 'Technology',
      icon: <FaLaptop />,
      description: 'Develop and maintain our online learning platform. Experience with React, Node.js, and educational technology required.',
      fullDescription: 'We are seeking a talented Full-Stack Developer to build and maintain our online learning platform. The ideal candidate will have experience in educational technology and modern web development frameworks.',
      responsibilities: [
        'Develop and maintain web-based learning platform',
        'Build interactive features for student engagement',
        'Integrate video conferencing and collaboration tools',
        'Implement learning management system (LMS) features',
        'Optimize platform performance and user experience',
        'Ensure platform security and data protection',
        'Collaborate with academic team for feature requirements',
        'Provide technical support and troubleshooting'
      ],
      requirements: [
        'Bachelor\'s degree in Computer Science or related field',
        'Minimum 3 years of full-stack development experience',
        'Proficiency in React, Node.js, and JavaScript',
        'Experience with educational technology platforms',
        'Knowledge of MongoDB, PostgreSQL, or similar databases',
        'Familiarity with AWS or cloud platforms',
        'Understanding of RESTful APIs and microservices',
        'Strong problem-solving and debugging skills'
      ],
      benefits: [
        'Competitive salary package',
        'Remote work flexibility',
        'Latest technology stack',
        'Learning and development budget',
        'Health insurance',
        'Innovative work culture'
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink">
      {selectedJob ? (
        // Job Description Page
        <div className="min-h-screen bg-white">
          {/* Back Navigation */}
          <div className="bg-gradient-to-br from-primary/10 via-white to-white border-b border-primary/10">
            <div className="mx-auto max-w-6xl px-4 py-6 lg:px-6">
              <button
                onClick={() => setSelectedJob(null)}
                className="flex items-center gap-2 text-primary hover:text-primary-soft transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Positions
              </button>
            </div>
          </div>

          {/* Job Header */}
          <section className="border-b border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white">
            <div className="mx-auto max-w-6xl px-4 py-12 lg:px-6">
              <div className="flex items-start gap-6">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-2xl text-primary shadow-lg flex-shrink-0">
                  {selectedJob.icon}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-semibold text-ink mb-3">{selectedJob.title}</h1>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <span className="px-3 py-1.5 rounded-full bg-primary/10 text-sm font-medium text-primary">{selectedJob.type}</span>
                    <span className="px-3 py-1.5 rounded-full bg-primary/10 text-sm font-medium text-primary">{selectedJob.department}</span>
                    <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-sm font-medium text-primary">
                      <FaMapMarkerAlt />
                      {selectedJob.location}
                    </span>
                  </div>
                  <p className="text-lg text-ink-muted leading-relaxed">{selectedJob.fullDescription}</p>
                </div>
              </div>
              <div className="mt-8">
                <button
                  onClick={() => {
                    setSelectedJob(null);
                    setTimeout(() => {
                      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] inline-flex items-center gap-2"
                >
                  <FaPaperPlane />
                  Apply for this Position
                </button>
              </div>
            </div>
          </section>

          {/* Job Details */}
          <section className="py-16">
            <div className="mx-auto max-w-6xl px-4 lg:px-6">
              <div className="grid gap-12 lg:grid-cols-3">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-10">
                  {/* Responsibilities */}
                  <div>
                    <h2 className="text-2xl font-semibold text-ink mb-6 flex items-center gap-3">
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary-soft rounded-full"></div>
                      Key Responsibilities
                    </h2>
                    <ul className="space-y-3">
                      {selectedJob.responsibilities.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-ink-muted">
                          <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h2 className="text-2xl font-semibold text-ink mb-6 flex items-center gap-3">
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary-soft rounded-full"></div>
                      Requirements & Qualifications
                    </h2>
                    <ul className="space-y-3">
                      {selectedJob.requirements.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-ink-muted">
                          <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Benefits */}
                  <div className="rounded-2xl border border-primary/10 bg-gradient-to-br from-primary/5 to-white p-6 shadow-elevated">
                    <h3 className="text-xl font-semibold text-ink mb-4">Benefits & Perks</h3>
                    <ul className="space-y-3">
                      {selectedJob.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-ink-muted">
                          <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Quick Apply */}
                  <div className="rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated">
                    <h3 className="text-xl font-semibold text-ink mb-3">Ready to Apply?</h3>
                    <p className="text-sm text-ink-muted mb-4">
                      Submit your application and join our team of passionate educators and professionals.
                    </p>
                    <button
                      onClick={() => {
                        setSelectedJob(null);
                        setTimeout(() => {
                          document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_12px_24px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_18px_30px_rgba(18,10,56,0.35)]"
                    >
                      Apply Now
                    </button>
                  </div>

                  {/* Share Job */}
                  {/* <div className="rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated">
                    <h3 className="text-lg font-semibold text-ink mb-3">Share this Job</h3>
                    <p className="text-sm text-ink-muted mb-4">
                      Know someone perfect for this role? Share it with them!
                    </p>
                    <div className="flex gap-2">
                      <button className="flex-1 rounded-lg border border-primary/20 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 transition-colors">
                        LinkedIn
                      </button>
                      <button className="flex-1 rounded-lg border border-primary/20 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 transition-colors">
                        Email
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        // Job Listings Page
        <>
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 bg-gradient-to-br from-primary/10 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,64,198,0.1),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(79,64,198,0.08),transparent_50%)]" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-6 px-4 py-20 text-center lg:px-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary-soft/20 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-5xl font-semibold sm:text-6xl bg-gradient-to-r from-primary to-primary-soft bg-clip-text text-transparent pb-2">
              Career Opportunities
            </h1>
          </div>
          <p className="max-w-3xl text-lg leading-relaxed text-ink-muted sm:text-xl">
            Passionate about teaching languages? Join us. We're looking for dedicated educators and professionals 
            to help build global communicators.
          </p>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Open Positions
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">Join Our Team</h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Explore current job openings and find the perfect role for your skills and passion
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {positions.map((position, index) => (
              <div
                key={index}
                onClick={() => setSelectedJob(position)}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)] cursor-pointer"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl text-primary mb-4 group-hover:scale-110 transition-transform">
                  {position.icon}
                </div>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-ink group-hover:text-primary transition-colors">{position.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">{position.type}</span>
                  <span className="px-2 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">{position.department}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-ink-muted mb-3">
                  <FaMapMarkerAlt className="text-primary" />
                  <span>{position.location}</span>
                </div>
                <p className="text-sm text-ink-muted leading-relaxed mb-4">{position.description}</p>
                <div className="flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
                  View Details
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upload Resume Form */}
      <section className="py-20 bg-canvas" id="application-form">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold mb-4">Submit Your Application</h2>
              <p className="text-ink-muted">
                Don't see a position that matches? Upload your resume and we'll keep you in mind for future opportunities
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="+91 90000 00000"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-ink mb-2">
                    Position of Interest
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a position</option>
                    {positions.map((pos, idx) => (
                      <option key={idx} value={pos.title}>{pos.title}</option>
                    ))}
                    <option value="other">Other / General Application</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-ink mb-2">
                  Years of Experience
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select experience level</option>
                  <option value="0-1">0-1 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-ink mb-2">
                  Upload Resume <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  required
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                />
                <p className="mt-2 text-xs text-ink-muted">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-ink mb-2">
                  Cover Letter / Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Tell us why you're interested in joining our team, your relevant experience, and what you can bring to The Lingua Alliance..."
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] flex items-center justify-center gap-2"
              >
                <FaPaperPlane />
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>
        </>
      )}
    </div>
  );
};

export default Career;

